import IconPencilSolid from '@/assets/icons/font_awesome/pencil-solid.svg?react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { toast } from '@/hooks/use-toast';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_workspace_update_avatar } from '@/state/workspace/entity/remote_workspace_update_avatar';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { upload_image_single } from '@/utils/utils_upload';
import { DTO__file_data } from '@shared/dto/+image/image_asset_base/DTO__file_data';
import { RequestWorkspaceUpdateAvatar } from '@shared/dto/+workspace/workspace_entity/_requests/request_workspace_update_avatar';
import { DTO__workspace } from '@shared/dto/+workspace/workspace_entity/DTO__workspace';
import { get_avatar_fallback_from_name } from '@shared/utils/utils_string';
import { ElementRef, FC, forwardRef, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends PropsWithChildren {
  workspace: DTO__workspace;
  className?: string;
}

// info: delete fallback for relativePath of avatar and name after fixing bug with empty workspaces on different pages
export const WorkspaceAvatarEdit: FC<Props> = forwardRef<ElementRef<typeof Avatar>, Props>(({ workspace, className, children, ...props }, ref) => {
  const dispatch = use_dispatch();
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const [fallback, setFallback] = useState('');

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (workspace.is_stub()) return;

    setFallback(get_avatar_fallback_from_name(workspace.name));
  }, [workspace]);

  /*
   * EVENTS
   */

  function onClick() {
    upload_image_single(toast, on_image_uploaded, t);
  }

  async function on_image_uploaded(file_data: DTO__file_data) {
    const requestData = new RequestWorkspaceUpdateAvatar();
    requestData.workspace_id = workspace._id;
    requestData.file_data = file_data;

    await dispatch(remote_workspace_update_avatar(requestData));
  }

  /*
   * HTML
   */

  return (
    <Avatar className="!w-[5rem] !h-[5rem] !cursor-pointer flex items-center justify-center !rounded-xl" onClick={onClick} ref={ref} {...props}>
      <div className="absolute size-full flex justify-center items-center backdrop-blur-sm backdrop-brightness-50 opacity-0 hover:opacity-100 duration-300">
        <IconPencilSolid className="!w-5 !h-5 !cursor-pointer" />
      </div>
      <AvatarImage src={path_relative_to_absolute(workspace.url_relative_avatar)} />
      <AvatarFallback className="!text-[20px] ">{fallback}</AvatarFallback>
    </Avatar>
  );
});
